import React, { useState, useEffect, useRef } from 'react';
import { copyToClipboard, readFromClipboard } from '../utils/helpers';
import { Message, Conversation, UserData } from '../types';
import { CloseIcon, CopyIcon, PasteIcon, ImageIcon, CheckmarkIcon } from './Icons';
import { TrashIcon } from './Icons';
import { uploadMediaFile, compressImage } from '../utils/mediaUtils';
import { handleSliderPointerDown } from '../utils/helpers';
import { stickerTextStyle } from './StickerText';
import { SlideshowBuilder } from './SlideshowBuilder';

const hexToRgba = (hex: string, opacity: number) => {
    try {
        let cleanHex = hex.replace('#', '');
        if (cleanHex.length === 3) {
            cleanHex = cleanHex[0] + cleanHex[0] + cleanHex[1] + cleanHex[1] + cleanHex[2] + cleanHex[2];
        }
        const r = parseInt(cleanHex.substring(0, 2), 16);
        const g = parseInt(cleanHex.substring(2, 4), 16);
        const b = parseInt(cleanHex.substring(4, 6), 16);
        return isNaN(r) || isNaN(g) || isNaN(b) ? null : `rgba(${r}, ${g}, ${b}, ${opacity})`;
    } catch (e) {
        return null;
    }
};

export interface NewConversationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onStartConversation: (userId: string) => void;
    suggestedUserIds: string[];
    usersData: Record<string, UserData>;
}

export const NewConversationModal = ({ isOpen, onClose, onStartConversation, suggestedUserIds, usersData }: NewConversationModalProps) => {
    const [search, setSearch] = useState('');
    const searchRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!isOpen) {
            setSearch('');
        } else {
            setTimeout(() => searchRef.current?.focus(), 100);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const userIdsToShow = search 
        ? suggestedUserIds.filter(uid => 
            usersData[uid]?.displayName?.toLowerCase().includes(search.toLowerCase()) || 
            uid.toLowerCase().includes(search.toLowerCase())
          )
        : suggestedUserIds;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[80]" onClick={onClose}>
            <div className="rounded-2xl shadow-lg p-6 max-w-sm w-full mx-4 h-[70dvh] flex flex-col backdrop-blur-md" style={{ backgroundColor: 'var(--app-theme-bg)' }} onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4 flex-shrink-0">
                    <h2 className="text-xl font-bold">New Message</h2>
                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
                        <CloseIcon className="w-5 h-5 text-gray-400" />
                    </button>
                </div>
                <input
                    ref={searchRef}
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search users..."
                    className="w-full mb-3 p-2 rounded-full text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
                />
                <div className="flex-grow overflow-y-auto mb-4 border-y border-gray-700 -mx-6 px-4 py-2">
                    <ul className="space-y-1">
                        {userIdsToShow.length === 0 && <div className="text-gray-400 text-center p-2">No users found.</div>}
                        {userIdsToShow.filter(Boolean).map(uid => {
                            const user = usersData?.[uid];
                            const firstChar = uid ? uid[0]?.toUpperCase() : '?';
                            return (
                                <li key={uid}>
                                    <button onClick={() => { onStartConversation(uid); onClose(); }} className={`w-full text-left flex items-center gap-3 py-2 px-2 hover:bg-black/20 transition-colors rounded-full`}>
                                        {user?.pfp ? (
                                            <img src={user.pfp} alt={uid} className="w-9 h-9 rounded-full object-cover" />
                                        ) : (
                                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                                                {firstChar}
                                            </div>
                                        )}
                                        <div className="flex flex-col flex-1 min-w-0">
                                            <span className="text-sm font-semibold truncate text-white">{user?.displayName || uid}</span>
                                            {user?.displayName && <span className="text-[10px] text-gray-400 truncate">@{uid}</span>}
                                        </div>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </div>
    );
};


interface ForwardMessageModalProps {
    isOpen: boolean;
    onClose: () => void;
    message: Message;
    conversations: Conversation[];
    currentUserId: string;
    onForward: (message: Message, targetConversationIds: number[]) => void;
    usersData: Record<string, UserData>;
}

export const ForwardMessageModal = ({ isOpen, onClose, message, conversations, currentUserId, onForward, usersData }: ForwardMessageModalProps) => {
    const [selectedConvos, setSelectedConvos] = useState<Set<number>>(new Set());

    const toggleSelection = (convoId: number) => {
        setSelectedConvos(prev => {
            const newSet = new Set(prev);
            if (newSet.has(convoId)) {
                newSet.delete(convoId);
            } else {
                newSet.add(convoId);
            }
            return newSet;
        });
    };

    const handleForward = () => {
        onForward(message, Array.from(selectedConvos));
        onClose();
    };

    const [search, setSearch] = useState('');
    const searchRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!isOpen) {
            setSelectedConvos(new Set());
            setSearch('');
        } else {
            searchRef.current?.focus();
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const convosToShow = conversations.filter(convo => {
        const otherUserId = convo.participants.find(p => p !== currentUserId);
        if (!otherUserId) return false;
        const displayName = convo.nicknames?.[otherUserId] || usersData[otherUserId]?.displayName || otherUserId;
        return displayName.toLowerCase().includes(search.toLowerCase()) || otherUserId.toLowerCase().includes(search.toLowerCase());
    });

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[80]" onClick={onClose}>
            <div className="rounded-[2.5rem] w-full max-w-md overflow-hidden flex flex-col shadow-lg p-6 mx-4 h-[70dvh] backdrop-blur-md" style={{ backgroundColor: 'var(--app-theme-bg)' }} onClick={e => e.stopPropagation()}>
                <h2 className="text-xl font-bold mb-4 flex-shrink-0">Forward Message to...</h2>
                <input
                    ref={searchRef}
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search users..."
                    className="w-full mb-3 p-2 rounded-full text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
                />
                <div className="flex-grow overflow-y-auto mb-4 border-y border-gray-700 -mx-6 px-4 py-2">
                    <ul className="space-y-1">
                        {convosToShow.length === 0 && <div className="text-gray-400 text-center p-2">No conversations found.</div>}
                        {convosToShow.map(convo => {
                            const otherUserId = convo.participants.find(p => p !== currentUserId)!;
                            const isSelected = selectedConvos.has(convo.id);
                            return (
                                <li key={convo.id}>
                                    <button onClick={() => toggleSelection(convo.id)} className={`w-full text-left flex items-center gap-3 py-2 px-2 hover:bg-black/20 transition-colors rounded-full`}>
                                        <div className={`flex-none w-9 h-9 min-w-[36px] min-h-[36px] rounded-full overflow-hidden flex items-center justify-center transition-colors ${isSelected ? 'border-transparent' : 'bg-transparent border border-white/10'}`}>
                                            <img
                                                src="/assets/checkmark.webp"
                                                alt="selected"
                                                onError={(e: any) => { e.currentTarget.style.display = 'none'; }}
                                                style={{
                                                    transform: isSelected ? 'scale(1)' : 'scale(0.7)',
                                                    transition: 'transform 450ms cubic-bezier(.2,.9,.2,1), opacity 450ms',
                                                    opacity: isSelected ? 1 : 0
                                                }}
                                                className="w-6 h-6"
                                            />
                                        </div>
                                        <img src={usersData[otherUserId]?.pfp} alt={otherUserId} className="w-10 h-10 rounded-full object-cover" />
                                        <span className="font-semibold text-sm truncate">{convo.nicknames?.[otherUserId] || usersData[otherUserId]?.displayName || otherUserId}</span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
                <div className="flex justify-end gap-4 flex-shrink-0">
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-white font-bold py-2 px-6 rounded-full transition-colors">Cancel</button>
                    <button type="button" onClick={handleForward} disabled={selectedConvos.size === 0} className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-500 text-white font-bold py-2 px-6 rounded-full transition-colors">Forward</button>
                </div>
            </div>
        </div>
    );
};

export const DeleteMessageModal = ({ isOpen, onClose, onConfirm, messageText }: { isOpen: boolean, onClose: () => void, onConfirm: () => void, messageText?: string }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[80]" onClick={e => e.stopPropagation()}>
            <div className="rounded-2xl shadow-lg p-8 max-w-md w-full mx-4 text-center backdrop-blur-md" style={{ backgroundColor: 'var(--app-theme-bg)' }}>
                <h2 className="text-2xl font-bold mb-4 text-red-500">Delete Message</h2>
                <p className="text-gray-300 mb-6">
                    {messageText ? (
                        <>Are you sure you want to permanently delete: <span className="italic">"{messageText}"</span>?</>
                    ) : (
                        <>Are you sure you want to permanently delete this message?</>
                    )}
                </p>
                <div className="flex justify-center gap-4">
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-white font-bold py-2 px-6 rounded-full transition-colors">Cancel</button>
                    <button type="button" onClick={onConfirm} className="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-6 rounded-full transition-colors">Delete</button>
                </div>
            </div>
        </div>
    );
};

export const ConversationThemeModal = ({
    isOpen,
    onClose,
    activeConversation,
    onUpdateConversation,
    currentUser,
    usersData,
    onDeleteConversation
}: {
    isOpen: boolean;
    onClose: () => void;
    activeConversation: Conversation;
    onUpdateConversation: (updates: Partial<Conversation>) => void,
    currentUser: string | null;
    usersData: Record<string, UserData>;
    onDeleteConversation: (convoId: number) => void;
}) => {
    const currentUserData = currentUser ? usersData[currentUser] : null;
    const currentTheme = activeConversation.theme;
    const [textColor, setTextColor] = useState(currentTheme?.textColor || '#ffffff');
    const [sentBubbleColor, setSentBubbleColor] = useState(currentTheme?.sentBubbleColor || '#2563eb');
    const [receivedBubbleColor, setReceivedBubbleColor] = useState(currentTheme?.receivedBubbleColor || '#4b5563');
    const [backgroundColor, setBackgroundColor] = useState(currentTheme?.backgroundColor || '#0f172a');

    // Default to 'cover' if style is missing
    const initialStyle = currentTheme?.backgroundImageStyle || 'cover';
    const [bgStyle, setBgStyle] = useState<string>(initialStyle);

    const [backgroundResolution, setBackgroundResolution] = useState(currentTheme?.backgroundResolution ?? 100);
    const [backgroundMode, setBackgroundMode] = useState<'image' | 'color'>(currentTheme?.backgroundMode || 'color');
    const [backgroundSlideshow, setBackgroundSlideshow] = useState<{
        enabled: boolean;
        attachments: { id: string; url: string; type: string; name?: string }[];
        intervalValue: number;
        intervalUnit: 'seconds' | 'minutes' | 'hours' | 'days';
    }>(() => {
        const slideshow = currentTheme?.backgroundSlideshow || { enabled: true, attachments: [], intervalValue: 10, intervalUnit: 'seconds' };
        if (currentTheme?.backgroundImage && (!slideshow.attachments || slideshow.attachments.length === 0)) {
            return {
                ...slideshow,
                attachments: [
                    { id: `legacy-${Date.now()}`, url: currentTheme.backgroundImage, type: 'image', name: 'Legacy Background' }
                ]
            };
        }
        return slideshow;
    });
    const [bubbleOpacity, setBubbleOpacity] = useState(currentTheme?.bubbleOpacity ?? 100);

    const nativeSlideshowInputRef = useRef<HTMLInputElement>(null);
    const [isUploadingBg, setIsUploadingBg] = useState(false);

    const [hideBubbles, setHideBubbles] = useState(activeConversation.hideBubbles || false);
    const [yipYapName, setYipYapName] = useState(activeConversation.yipYapName || '');
    const [savingUsers, setSavingUsers] = useState<Set<string>>(new Set());
    const [nicknames, setNicknames] = useState<Record<string, string>>(() => {
        const existing = { ...(activeConversation.nicknames || {}) };
        activeConversation.participants.forEach(p => {
            if (!existing[p]) {
                existing[p] = usersData[p]?.displayName || p;
            }
        });
        return existing;
    });

    const styleOptions = ['color', 'tile'];

    // Sync local state with props when the conversation theme changes externally
    useEffect(() => {
        setTextColor(currentTheme?.textColor || '#ffffff');
        setSentBubbleColor(currentTheme?.sentBubbleColor || '#2563eb');
        setReceivedBubbleColor(currentTheme?.receivedBubbleColor || '#4b5563');
        setBackgroundColor(currentTheme?.backgroundColor || '#0f172a');
        setBackgroundResolution(currentTheme?.backgroundResolution ?? 100);
        setBackgroundMode(currentTheme?.backgroundMode || 'color');
        const initialSlideshow = currentTheme?.backgroundSlideshow || { enabled: true, attachments: [], intervalValue: 10, intervalUnit: 'seconds' };
        if (currentTheme?.backgroundImage && (!initialSlideshow.attachments || initialSlideshow.attachments.length === 0)) {
            setBackgroundSlideshow({
                ...initialSlideshow,
                attachments: [
                    { id: `legacy-${Date.now()}`, url: currentTheme.backgroundImage, type: 'image', name: 'Legacy Background' }
                ]
            });
        } else {
            setBackgroundSlideshow(initialSlideshow);
        }
        setBubbleOpacity(currentTheme?.bubbleOpacity ?? 100);
        setBgStyle(currentTheme?.backgroundImageStyle || 'cover');
        setYipYapName(activeConversation.yipYapName || '');
    }, [currentTheme, activeConversation.yipYapName]);

    // Realtime persistence helper
    const persistUpdates = (updates?: any) => {
        // Build the latest theme state by merging current theme with all local state and new updates
        const latestTheme = {
            textColor,
            sentBubbleColor,
            receivedBubbleColor,
            backgroundColor,
            backgroundImageStyle: bgStyle as 'tile' | 'cover' | 'color',
            backgroundResolution,
            backgroundMode,
            backgroundSlideshow,
            bubbleOpacity,
            backgroundImage: '', // Explicitly clear the legacy background image field
            ...(updates?.theme || {})
        };

        onUpdateConversation({
            ...updates,
            theme: latestTheme,
            hideBubbles: updates?.hideBubbles !== undefined ? updates.hideBubbles : hideBubbles,
            nicknames: updates?.nicknames !== undefined ? updates.nicknames : nicknames,
        });
    };

    const handleBgStyleChange = (style: string) => {
        setBgStyle(style);

        // Immediately persist the style change without losing data
        persistUpdates({
            theme: {
                backgroundImageStyle: style as 'tile' | 'color',
            }
        });
    };

    const handleColorChange = (c: string, type: 'bg' | 'text' | 'sent' | 'received') => {
        if (type === 'bg') {
            setBackgroundColor(c);
            persistUpdates({ theme: { backgroundColor: c } });
        } else if (type === 'text') {
            setTextColor(c);
            persistUpdates({ theme: { textColor: c } });
        } else if (type === 'sent') {
            setSentBubbleColor(c);
            persistUpdates({ theme: { sentBubbleColor: c } });
        } else if (type === 'received') {
            setReceivedBubbleColor(c);
            persistUpdates({ theme: { receivedBubbleColor: c } });
        }
    };

    const handleNativeSlideshowUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0 || !currentUser) return;

        const newAttachments = [...(backgroundSlideshow.attachments || [])];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const isVideo = file.type.startsWith('video/');
            const tempUrl = URL.createObjectURL(file) + (isVideo ? "#video" : "");
            const tempId = `temp-${Date.now()}-${i}`;
            
            newAttachments.push({
                id: tempId,
                url: tempUrl,
                type: isVideo ? 'video' : 'image',
                name: file.name
            });
        }

        const tempSlideshow = { ...backgroundSlideshow, attachments: newAttachments };
        setBackgroundSlideshow(tempSlideshow);
        persistUpdates({ theme: { backgroundSlideshow: tempSlideshow } });

        try {
            const uploadedAttachments = [...(backgroundSlideshow.attachments || [])];
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const isVideo = file.type.startsWith('video/');
                const isPreservedType = isVideo || ['image/gif', 'image/webp', 'image/apng'].includes(file.type) || /\.(gif|webp|mp4|webm|ogg|mov|m4v)$/i.test(file.name);
                let fileToUpload = file;
                if (!isPreservedType) {
                    try {
                        const compressedBlob = await compressImage(file);
                        fileToUpload = new File([compressedBlob], file.name.replace(/\.[^/.]+$/, "") + ".png", { type: 'image/png' });
                    } catch (e) {
                        console.error("Compression failed", e);
                    }
                }
                const url = await uploadMediaFile(fileToUpload, currentUser);
                if (url) {
                    uploadedAttachments.push({
                        id: `attachment-${Date.now()}-${i}`,
                        url: url,
                        type: isVideo ? 'video' : 'image',
                        name: file.name
                    });
                }
            }
            const finalSlideshow = { ...backgroundSlideshow, attachments: uploadedAttachments };
            setBackgroundSlideshow(finalSlideshow);
            persistUpdates({ theme: { backgroundSlideshow: finalSlideshow } });
        } catch (err) {
            console.error("Multiple upload failed", err);
        }
    };



    if (!isOpen) return null;

    const userThemeColor = currentUserData?.appThemeColor || '#161b22';
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity ?? 50) / 100;
    const modalBg = hexToRgba(userThemeColor, userOpacity) || 'var(--app-theme-bg)';

    return (
        <div className="messages-page-no-swipe fixed inset-0 flex items-center justify-center z-[80] p-4 animate-fade-in bg-black/70 backdrop-blur-sm" onClick={onClose}>
            <div
                className="rounded-[2.5rem] w-full max-w-md overflow-hidden flex flex-col relative z-10 border border-white/5 mx-auto backdrop-blur-md"
                style={{
                    backgroundColor: modalBg,
                    color: userTextColor,
                    height: '85vh',
                    maxHeight: '800px'
                }}
                onClick={e => e.stopPropagation()}
            >
                <div className="flex justify-between items-center p-6 border-b border-white/5 bg-black/20 shrink-0">
                    <h2 className="text-2xl font-black uppercase tracking-tighter italic" style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}>
                        {activeConversation.isYipYap ? 'Channel Settings' : 'Chat Settings'}
                    </h2>
                    <button onClick={onClose} className="p-2.5 bg-white/10 hover:bg-white/20 rounded-full transition-colors active:scale-95 shadow-sm">
                        <CloseIcon className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-grow overflow-y-auto px-6 py-4 space-y-6 scrollbar-none allow-scroll">
                    <div className="space-y-6">
                        {activeConversation.isYipYap && (
                            <div className="space-y-4">
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-4 px-1" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Channel Info</p>
                                <div className="space-y-1">
                                    <p className="text-[10px] text-gray-400 uppercase">Channel Name</p>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={yipYapName}
                                            onChange={(e) => setYipYapName(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && persistUpdates({ yipYapName })}
                                            className="w-full bg-gray-900/50 border border-gray-700 rounded-full pl-3 pr-10 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-blue-500 transition-colors"
                                        />
                                        <button
                                            onClick={() => {
                                                persistUpdates({ yipYapName });
                                                setSavingUsers(prev => new Set(prev).add('yipyap_name'));
                                                setTimeout(() => {
                                                    setSavingUsers(prev => {
                                                        const next = new Set(prev);
                                                        next.delete('yipyap_name');
                                                        return next;
                                                    });
                                                }, 2000);
                                            }}
                                            className={`absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center transition-all ${savingUsers.has('yipyap_name') ? 'bg-green-500 text-white' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}
                                            title="Save name"
                                        >
                                            {savingUsers.has('yipyap_name') ? (
                                                <img src="/assets/checkmark.webp" alt="Saved" className="w-4 h-4 object-contain" />
                                            ) : (
                                                <CheckmarkIcon className="w-4 h-4" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {!activeConversation.isYipYap || activeConversation.yipYapMode !== 'anonymous' ? (
                            <div className="space-y-4 pt-4 border-t border-white/5">
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-4 px-1" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>General</p>
                                <label className="flex items-center justify-between cursor-pointer group">
                                    <span className="text-sm font-medium text-gray-200">Hide Chat Bubbles</span>
                                    <input
                                        type="checkbox"
                                        checked={hideBubbles}
                                        onChange={(e) => {
                                            setHideBubbles(e.target.checked);
                                            persistUpdates({ hideBubbles: e.target.checked });
                                        }}
                                        className="sr-only peer"
                                    />
                                    <div className="relative w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                </label>
                                <div className="space-y-4 pt-1">
                                    <div className="flex justify-between items-center">
                                        <label className="text-[9px] font-bold uppercase" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Chat Bubble Transparency</label>
                                        <span className="text-xs font-black text-blue-400">{bubbleOpacity}%</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="0"
                                        max="100"
                                        value={bubbleOpacity}
                                        onPointerDown={(e) => handleSliderPointerDown(e, bubbleOpacity, 0, 100)}
                                        onChange={(e) => {
                                            const val = Number(e.target.value);
                                            setBubbleOpacity(val);
                                            persistUpdates({ theme: { bubbleOpacity: val } });
                                        }}
                                        className="w-full h-1.5 bg-black/40 rounded-lg appearance-none cursor-pointer accent-blue-500"
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                                <p className="text-[10px] text-blue-400 font-bold uppercase tracking-wider mb-1">Anonymous Hall</p>
                                <p className="text-[11px] text-gray-400 leading-relaxed">Chat bubbles and real identities are hidden for maximum anonymity. 👻</p>
                            </div>
                        )}

                        {(!activeConversation.isYipYap || activeConversation.yipYapMode !== 'anonymous') && (
                            <div className="space-y-4 pt-4 border-t border-white/5">
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-4 px-1" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Nicknames</p>
                                {activeConversation.participants.map(userId => {
                                    const user = usersData[userId];
                                    if (!user) return null;
                                    return (
                                        <div key={userId} className="space-y-1">
                                            <p className="text-[10px] text-gray-400 uppercase">{user.displayName} {userId === currentUser ? '(You)' : ''}</p>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    placeholder="Set nickname..."
                                                    value={nicknames[userId] || ''}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setNicknames(prev => ({ ...prev, [userId]: val }));
                                                    }}
                                                    className="w-full bg-gray-900/50 border border-gray-700 rounded-full pl-3 pr-10 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-blue-500 transition-colors"
                                                />
                                                <button
                                                    onClick={() => {
                                                        persistUpdates({ nicknames });
                                                        setSavingUsers(prev => new Set(prev).add(userId));
                                                        setTimeout(() => {
                                                            setSavingUsers(prev => {
                                                                const next = new Set(prev);
                                                                next.delete(userId);
                                                                return next;
                                                            });
                                                        }, 2000);
                                                    }}
                                                    className={`absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center transition-all ${savingUsers.has(userId) ? 'bg-green-500 text-white' : 'bg-blue-600 hover:bg-blue-500 text-white'}`}
                                                    title="Save nickname"
                                                >
                                                    {savingUsers.has(userId) ? (
                                                        <img src="/assets/checkmark.webp" alt="Saved" className="w-4 h-4 object-contain animate-in zoom-in duration-300" />
                                                    ) : (
                                                        <CheckmarkIcon className="w-4 h-4" />
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <div className="space-y-4 pt-4 border-t border-white/5">
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-4 px-1" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Colors</p>
                            <div className="space-y-4">
                                <ColorInput label="Background" value={backgroundColor} onChange={(c) => handleColorChange(c, 'bg')} />
                                <ColorInput label="Text" value={textColor} onChange={(c) => handleColorChange(c, 'text')} />
                                <ColorInput label="Sent Bubble" value={sentBubbleColor} onChange={(c) => handleColorChange(c, 'sent')} />
                                <ColorInput label="Received Bubble" value={receivedBubbleColor} onChange={(c) => handleColorChange(c, 'received')} />
                            </div>
                        </div>
                        <div className="space-y-4 pt-4 border-t border-white/5">
                            <div className="flex items-center justify-between mb-4 px-1">
                                <label className="text-[10px] font-black uppercase tracking-[0.2em]" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Background</label>
                                <div className="flex bg-black/40 p-1 rounded-xl border border-white/5">
                                    {(['image', 'color'] as const).map(mode => (
                                        <button
                                            key={mode}
                                            onClick={() => {
                                                setBackgroundMode(mode);
                                                persistUpdates({ theme: { backgroundMode: mode } });
                                            }}
                                            className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase transition-all ${backgroundMode === mode ? 'bg-white/20 text-white' : 'text-white hover:text-white'}`}
                                        >
                                            {mode}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {backgroundMode === 'image' ? (
                                <div className="space-y-4 animate-fade-in">
                                    <SlideshowBuilder
                                        slideshow={backgroundSlideshow}
                                        onChangeSlideshow={(s) => {
                                            setBackgroundSlideshow(s);
                                            persistUpdates({ theme: { backgroundSlideshow: s } });
                                        }}
                                        nativeInputRef={nativeSlideshowInputRef as React.RefObject<HTMLInputElement>}
                                        onNativeUpload={handleNativeSlideshowUpload}
                                    />
                                </div>
                            ) : (
                                <div className="space-y-4 animate-fade-in">
                                    <div className="w-full h-32 rounded-2xl border border-white/10 flex items-center justify-center relative transition-all overflow-hidden" style={{ backgroundColor: backgroundColor }}>
                                        <input
                                            type="color"
                                            value={backgroundColor}
                                            onChange={(e) => handleColorChange(e.target.value, 'bg')}
                                            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                        />
                                        <div className="p-3 bg-black/40 backdrop-blur-md rounded-xl border border-white/10 text-[10px] font-black uppercase tracking-widest pointer-events-none">
                                            Change Color
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="space-y-4 pt-1 px-2">
                                <div className="flex items-center justify-between">
                                    <label className="text-[9px] font-bold uppercase" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Background Style</label>
                                    <div className="flex bg-black/40 p-1 rounded-xl border border-white/5">
                                        {(['cover', 'tile'] as const).map(style => (
                                            <button
                                                key={style}
                                                onClick={() => handleBgStyleChange(style)}
                                                className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase transition-all ${bgStyle === style ? 'bg-white/20 text-white' : 'text-white hover:text-white'}`}
                                            >
                                                {style === 'cover' ? 'Fill' : 'Tile'}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {bgStyle === 'tile' && (
                                    <div className="space-y-4 animate-fade-in no-swipe">
                                        <div className="flex justify-between items-center">
                                            <label className="text-[9px] font-bold uppercase" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>Tile resolution</label>
                                            <div className="flex items-center gap-1">
                                                <input
                                                    type="number"
                                                    min="25"
                                                    max="200"
                                                    value={backgroundResolution}
                                                    onChange={(e) => {
                                                        const val = Number(e.target.value);
                                                        if (!isNaN(val) && val >= 25 && val <= 200) {
                                                            setBackgroundResolution(val);
                                                            persistUpdates({ theme: { backgroundResolution: val } });
                                                        }
                                                    }}
                                                    onPointerDown={(e) => e.stopPropagation()}
                                                    className="w-12 bg-white/5 border border-white/10 rounded-md text-xs font-black text-blue-400 text-center py-0.5"
                                                />
                                                <span className="text-xs font-black text-blue-400">%</span>
                                            </div>
                                        </div>
                                        <input
                                            type="range"
                                            min="25"
                                            max="200"
                                            value={backgroundResolution}
                                            onPointerDown={(e) => handleSliderPointerDown(e, backgroundResolution, 25, 200)}
                                            onChange={(e) => {
                                                const val = Number(e.target.value);
                                                setBackgroundResolution(val);
                                                persistUpdates({ theme: { backgroundResolution: val } });
                                            }}
                                            className="w-full h-1.5 bg-black/40 rounded-lg appearance-none cursor-pointer accent-blue-500"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                        {/* Danger Zone — bottom of settings */}
                        {(!activeConversation.isYipYap || activeConversation.yipYapOwner === currentUser) && (
                            <div className="space-y-4 pt-4 border-t border-red-500/20 mt-2">
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-4 px-1 text-red-500" style={stickerTextStyle(0.18)}>Danger Zone</p>
                                <button
                                    onClick={() => {
                                        if (window.confirm(`Are you sure you want to delete this ${activeConversation.isYipYap ? 'channel' : 'conversation'}? This action cannot be undone.`)) {
                                            onDeleteConversation(activeConversation.id);
                                            onClose();
                                        }
                                    }}
                                    className="w-full py-3 rounded-full bg-red-600/20 hover:bg-red-600/30 text-red-500 font-bold text-sm border border-red-500/30 transition-all active:scale-95 flex items-center justify-center gap-2"
                                >
                                    <TrashIcon className="w-4 h-4" />
                                    {activeConversation.isYipYap ? 'Delete Channel' : 'Delete Conversation'}
                                </button>
                            </div>
                        )}
                </div>
            </div>
        </div>
    );
};

const ColorInput = ({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) => (
    <div className="space-y-2">
        <label className="text-[9px] font-bold uppercase" style={{ color: 'var(--app-text-color)', ...stickerTextStyle(0.18) }}>{label}</label>
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full border border-white/20 relative cursor-pointer shadow-lg overflow-hidden flex-shrink-0" style={{ backgroundColor: value }}>
                <input type="color" value={value} onChange={e => onChange(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" />
            </div>
            <input
                type="text"
                value={value.toUpperCase()}
                onChange={e => {
                    const val = e.target.value;
                    if (val.startsWith('#')) onChange(val.substring(0, 7));
                    else if (val.length > 0) onChange('#' + val.substring(0, 6));
                    else onChange('');
                }}
                className="flex-1 min-w-0 bg-black/20 border border-white/5 rounded-xl py-2 px-4 text-xs font-mono focus:outline-none focus:border-white/30 transition-colors"
                maxLength={7}
                placeholder="#000000"
            />
            <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                    onClick={() => {
                        copyToClipboard(value);
                    }}
                    className="p-2.5 bg-black/20 border border-white/5 rounded-xl hover:bg-black/40 transition-all active:scale-95"
                    title="Copy Hex"
                >
                    <CopyIcon className="w-4 h-4 opacity-60" />
                </button>
                <button
                    onClick={async () => {
                        try {
                            const text = await readFromClipboard();
                            if (text && /^#([0-9A-F]{3}){1,2}$/i.test(text)) {
                                onChange(text);
                            } else if (text && text.length >= 3 && text.length <= 6 && !text.startsWith('#')) {
                                onChange('#' + text);
                            }
                        } catch (err) {
                            console.error('Failed to read clipboard', err);
                        }
                    }}
                    className="p-2.5 bg-black/20 border border-white/5 rounded-xl hover:bg-black/40 transition-all active:scale-95"
                    title="Paste Hex"
                >
                    <PasteIcon className="w-4 h-4 opacity-60" />
                </button>
            </div>
        </div>
    </div>
);

export interface YipYapCreateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: { name: string, mode: 'broadcast' | 'anonymous' | 'forum', visibility: 'public' | 'open', groupIds?: string[] }) => void;
    followerGroups?: any[];
    usersData?: Record<string, UserData>;
    communities?: any[];
    themeColor?: string;
    textColor?: string;
    opacity?: number;
    existingNonAnonymousYipYapNames?: string[];
}

export const YipYapCreateModal = ({ isOpen, onClose, onSubmit, followerGroups = [], usersData = {}, communities = [], themeColor = '#1b1b1f', textColor = '#ffffff', opacity = 80, existingNonAnonymousYipYapNames = [] }: YipYapCreateModalProps) => {

    const [name, setName] = useState('');
    const [mode, setMode] = useState<'broadcast' | 'anonymous' | 'forum'>('broadcast');
    const [visibility, setVisibility] = useState<'public' | 'open'>('public');
    const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState('');
    const [followersExpanded, setFollowersExpanded] = useState(true);
    const [communitiesExpanded, setCommunitiesExpanded] = useState(true);
    const [communityMembersExpanded, setCommunityMembersExpanded] = useState(true);
    const [groupsExpanded, setGroupsExpanded] = useState(true);
    const [selectedCommunityIds, setSelectedCommunityIds] = useState<Set<string>>(new Set());
    const [selectedChannelIds, setSelectedChannelIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (!isOpen) {
            setName('');
            setMode('broadcast');
            setVisibility('public');
            setSelectedGroupIds([]);
            setSelectedCommunityIds(new Set());
            setSelectedChannelIds(new Set());
            setError(null);
            setSearch('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSumbitLocal = () => {
        if (!name.trim()) return;
        if (mode !== 'anonymous' && existingNonAnonymousYipYapNames.includes(name.trim())) {
            setError("You already have a channel with this name. Please choose a different name for non-anonymous YipYaps.");
            return;
        }

        // Expand selected communities and channels into their member lists
        const finalGroupIds = [...selectedGroupIds];
        
        // Add all members from selected communities
        selectedCommunityIds.forEach(communityId => {
            const community = communities.find(c => c._id === communityId);
            if (community?.members) {
                community.members.forEach((member: string) => {
                    if (!finalGroupIds.includes(member)) {
                        finalGroupIds.push(member);
                    }
                });
            }
        });

        // Add all members from selected YipYap channels
        selectedChannelIds.forEach(channelId => {
            const channel = communities.find(c => c._id === channelId);
            if (channel?.members) {
                channel.members.forEach((member: string) => {
                    if (!finalGroupIds.includes(member)) {
                        finalGroupIds.push(member);
                    }
                });
            }
        });

        onSubmit({ name, mode, visibility, groupIds: finalGroupIds.length > 0 ? finalGroupIds : undefined });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[80] messages-page-no-swipe" onClick={onClose}>
            <div
                className="rounded-[2.5rem] shadow-lg p-7 max-w-sm w-full mx-4 backdrop-blur-md border border-white/10"
                style={{
                    backgroundColor: hexToRgba(themeColor, opacity / 100) || themeColor,
                    color: textColor
                }}
                onClick={e => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold" style={{ color: textColor }}>Start a YipYap</h2>

                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full text-gray-400"><CloseIcon className="w-5 h-5" /></button>
                </div>

                <div className="space-y-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 ml-1">Channel Name</label>
                        <input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="Enter channel name..."
                            className="w-full bg-black/40 border border-white/5 rounded-2xl p-4 text-white placeholder:text-gray-600 focus:outline-none focus:border-blue-500 transition-colors"
                        />
                        {error && (
                            <p className="text-red-400 text-xs mt-2 px-1 font-bold">{error}</p>
                        )}
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 ml-1">Type</label>
                        <div className="grid grid-cols-3 gap-2">
                            <button
                                onClick={() => setMode('broadcast')}
                                className={`p-4 rounded-2xl flex flex-col items-center gap-2 transition-all border-2 ${mode === 'broadcast' ? 'bg-blue-600 border-blue-500 text-white' : 'bg-white/5 border-transparent text-gray-400 hover:bg-white/10'}`}
                            >
                                <span className="text-lg">📢</span>
                                <span className="text-xs font-bold">Broadcast</span>
                            </button>
                            <button
                                onClick={() => setMode('forum')}
                                className={`p-4 rounded-2xl flex flex-col items-center gap-2 transition-all border-2 ${mode === 'forum' ? 'bg-green-600 border-green-500 text-white' : 'bg-white/5 border-transparent text-gray-400 hover:bg-white/10'}`}
                            >
                                <span className="text-lg">💬</span>
                                <span className="text-xs font-bold">Forum</span>
                            </button>
                            <button
                                onClick={() => setMode('anonymous')}
                                className={`p-4 rounded-2xl flex flex-col items-center gap-2 transition-all border-2 ${mode === 'anonymous' ? 'bg-purple-600 border-purple-500 text-white' : 'bg-white/5 border-transparent text-gray-400 hover:bg-white/10'}`}
                            >
                                <span className="text-lg">👻</span>
                                <span className="text-xs font-bold">Anonymous</span>
                            </button>
                        </div>
                        <p className="text-[10px] text-gray-600 px-1 mt-1">
                            {mode === 'broadcast' ? 'Only you can send messages. Others can only react.' : mode === 'anonymous' ? 'Anyone can reply pseudonymously. Names/PFPs are obscured.' : 'A group chat where members reply with their real profiles.'}
                        </p>
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 ml-1">Visibility</label>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                onClick={() => setVisibility('public')}
                                className={`p-4 rounded-2xl flex flex-col items-center gap-2 transition-all border-2 ${visibility === 'public' ? 'bg-amber-600 border-amber-500 text-white' : 'bg-white/5 border-transparent text-gray-400 hover:bg-white/10'}`}
                            >
                                <span className="text-lg">🌍</span>
                                <span className="text-xs font-bold">Public</span>
                            </button>
                            <button
                                onClick={() => setVisibility('open')}
                                className={`p-4 rounded-2xl flex flex-col items-center gap-2 transition-all border-2 ${visibility === 'open' ? 'bg-amber-600 border-amber-500 text-white' : 'bg-white/5 border-transparent text-gray-400 hover:bg-white/10'}`}
                            >
                                <span className="text-lg">🔓</span>
                                <span className="text-xs font-bold">Open</span>
                            </button>
                        </div>
                    </div>

                    {visibility === 'open' && (
                        <div className="space-y-2 animate-in slide-in-from-top-2 duration-300">
                            <label className="text-[10px] font-black uppercase tracking-widest text-gray-500 ml-1">Initial Members (Optional)</label>
                            
                            <input
                                type="text"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search users..."
                                className="w-full p-3 rounded-2xl bg-black/40 border border-white/5 text-white placeholder:text-gray-600 focus:outline-none focus:border-blue-500 transition-colors"
                            />
                            
                            <div className="max-h-[45vh] overflow-y-auto mb-4 space-y-3 touch-pan-y overscroll-contain pr-1 custom-scrollbar allow-scroll">
                                {/* Connections Section */}
                                <div className="space-y-2">
                                    <button
                                        onClick={() => setFollowersExpanded(!followersExpanded)}
                                        className="w-full flex items-center justify-between py-1.5 px-2 hover:bg-white/5 rounded-xl transition-colors select-none text-left"
                                        style={{ color: textColor }}
                                    >
                                        <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-60" style={{ ...stickerTextStyle(0.18) }}>Connections ({Object.keys(usersData).length})</span>
                                        <svg className={`w-3.5 h-3.5 transition-transform duration-200 transform ${followersExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                    {followersExpanded && (
                                        <div className="flex flex-wrap gap-2 pl-1">
                                            {Object.entries(usersData)
                                                .filter(([username]) => username.toLowerCase().includes(search.toLowerCase()))
                                                .map(([username, data]) => {
                                                    const isSelected = selectedGroupIds.includes(username);
                                                    return (
                                                        <button
                                                            key={username}
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedGroupIds(prev => 
                                                                    prev.includes(username) 
                                                                        ? prev.filter(id => id !== username)
                                                                        : [...prev, username]
                                                                );
                                                            }}
                                                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-colors border ${isSelected ? 'bg-blue-500/30 border-blue-400' : 'bg-black/30 border-white/10 hover:bg-white/10'}`}
                                                        >
                                                            {data?.pfp ? (
                                                                <img src={data.pfp} alt={username} className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
                                                            ) : (
                                                                <div className="w-5 h-5 rounded-full bg-gray-500 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">
                                                                    {(data?.displayName || username)[0].toUpperCase()}
                                                                </div>
                                                            )}
                                                            <span className="font-bold" style={stickerTextStyle(0.18)}>{data?.displayName || username}</span>
                                                        </button>
                                                    );
                                                })}
                                            {Object.keys(usersData).length === 0 && (
                                                <div className="text-gray-500 text-xs italic px-2">No connections yet</div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Communities Section */}
                                <div className="space-y-2">
                                    <button
                                        onClick={() => setCommunitiesExpanded(!communitiesExpanded)}
                                        className="w-full flex items-center justify-between py-1.5 px-2 hover:bg-white/5 rounded-xl transition-colors select-none text-left"
                                        style={{ color: textColor }}
                                    >
                                        <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-60" style={{ ...stickerTextStyle(0.18) }}>Communities ({communities.filter(c => !c.isYipYap).length})</span>
                                        <svg className={`w-3.5 h-3.5 transition-transform duration-200 transform ${communitiesExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                    {communitiesExpanded && (
                                        <div className="flex flex-wrap gap-2 pl-1">
                                            {communities
                                                .filter(c => !c.isYipYap && c.name.toLowerCase().includes(search.toLowerCase()))
                                                .map(community => {
                                                    const isSelected = selectedCommunityIds.has(community._id);
                                                    return (
                                                        <button
                                                            key={community._id}
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedCommunityIds(prev => {
                                                                    const newSet = new Set(prev);
                                                                    if (newSet.has(community._id)) {
                                                                        newSet.delete(community._id);
                                                                    } else {
                                                                        newSet.add(community._id);
                                                                    }
                                                                    return newSet;
                                                                });
                                                            }}
                                                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-colors border ${isSelected ? 'bg-blue-500/30 border-blue-400' : 'bg-black/30 border-white/10 hover:bg-white/10'}`}
                                                        >
                                                            <span className="font-bold" style={stickerTextStyle(0.18)}>{community.name}</span>
                                                        </button>
                                                    );
                                                })}
                                            {communities.filter(c => !c.isYipYap).length === 0 && (
                                                <div className="text-gray-500 text-xs italic px-2">No communities yet</div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* YipYap Channels Section */}
                                <div className="space-y-2">
                                    <button
                                        onClick={() => setCommunityMembersExpanded(!communityMembersExpanded)}
                                        className="w-full flex items-center justify-between py-1.5 px-2 hover:bg-white/5 rounded-xl transition-colors select-none text-left"
                                        style={{ color: textColor }}
                                    >
                                        <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-60" style={{ ...stickerTextStyle(0.18) }}>YipYap Channels ({communities.filter(c => c.isYipYap).length})</span>
                                        <svg className={`w-3.5 h-3.5 transition-transform duration-200 transform ${communityMembersExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                    {communityMembersExpanded && (
                                        <div className="flex flex-wrap gap-2 pl-1">
                                            {communities
                                                .filter(c => c.isYipYap && c.name.toLowerCase().includes(search.toLowerCase()))
                                                .map(channel => {
                                                    const isSelected = selectedChannelIds.has(channel._id);
                                                    return (
                                                        <button
                                                            key={channel._id}
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedChannelIds(prev => {
                                                                    const newSet = new Set(prev);
                                                                    if (newSet.has(channel._id)) {
                                                                        newSet.delete(channel._id);
                                                                    } else {
                                                                        newSet.add(channel._id);
                                                                    }
                                                                    return newSet;
                                                                });
                                                            }}
                                                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-colors border ${isSelected ? 'bg-blue-500/30 border-blue-400' : 'bg-black/30 border-white/10 hover:bg-white/10'}`}
                                                        >
                                                            <span className="font-bold" style={stickerTextStyle(0.18)}>{channel.name}</span>
                                                        </button>
                                                    );
                                                })}
                                            {communities.filter(c => c.isYipYap).length === 0 && (
                                                <div className="text-gray-500 text-xs italic px-2">No YipYap channels yet</div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Follower Groups Section */}
                                <div className="space-y-2">
                                    <button
                                        onClick={() => setGroupsExpanded(!groupsExpanded)}
                                        className="w-full flex items-center justify-between py-1.5 px-2 hover:bg-white/5 rounded-xl transition-colors select-none text-left"
                                        style={{ color: textColor }}
                                    >
                                        <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-60" style={{ ...stickerTextStyle(0.18) }}>Follower Groups ({followerGroups.length})</span>
                                        <svg className={`w-3.5 h-3.5 transition-transform duration-200 transform ${groupsExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                    {groupsExpanded && (
                                        <div className="flex flex-wrap gap-2 pl-1">
                                            {followerGroups
                                                .filter(group => group.name.toLowerCase().includes(search.toLowerCase()))
                                                .map(group => {
                                                    const isSelected = selectedGroupIds.includes(group.id);
                                                    return (
                                                        <button
                                                            key={group.id}
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedGroupIds(prev => 
                                                                    prev.includes(group.id) 
                                                                        ? prev.filter(id => id !== group.id)
                                                                        : [...prev, group.id]
                                                                );
                                                            }}
                                                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs transition-colors border ${isSelected ? 'bg-blue-500/30 border-blue-400' : 'bg-black/30 border-white/10 hover:bg-white/10'}`}
                                                        >
                                                            <span className="font-bold" style={stickerTextStyle(0.18)}>{group.name}</span>
                                                        </button>
                                                    );
                                                })}
                                            {followerGroups.length === 0 && (
                                                <div className="text-gray-500 text-xs italic px-2">No follower groups yet</div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {Object.keys(usersData).length === 0 && communities.length === 0 && followerGroups.length === 0 && (
                                    <div className="px-1 font-black italic uppercase tracking-tighter" style={{ ...stickerTextStyle(0.18), color: textColor, opacity: 0.5 }}>No members found.</div>
                                )}
                            </div>
                            
                            <p className="text-[10px] text-gray-600 px-1 mt-1 italic">
                                Members of selected groups will be invited initially.
                            </p>
                        </div>
                    )}

                    <button
                        onClick={handleSumbitLocal}
                        disabled={!name.trim()}
                        className={`w-full py-4 rounded-3xl font-black uppercase tracking-[0.2em] transition-all ${!name.trim() ? 'bg-gray-800 text-gray-600 cursor-not-allowed opacity-50' : 'bg-blue-600 text-white hover:bg-blue-500 shadow-lg shadow-blue-600/20 active:scale-95'}`}
                    >
                        Create YipYap
                    </button>
                </div>
            </div>
        </div>
    );
};
